<template>
  <body-portal>
    <base-modal
      :show.sync="visible"
      modal-classes="modal-dialog-centered modal-md user-profile-modal"
      body-classes="p-0"
    >
    <base-card header-classes="bg-white pb-5" body-classes="px-lg-3 py-lg-3" class="border-0">
      <div class="user-profile-modal__header">
        <h4 class="user-profile-modal__title">Minha conta</h4>
        <button
          type="button"
          class="user-profile-modal__close"
          aria-label="Fechar"
          @click="close"
        >
          <i class="fas fa-times" />
        </button>
      </div>

      <div class="user-profile-modal__photo-wrap">
        <img
          v-if="photoUrl"
          :src="photoUrl"
          :alt="`Foto de ${animalLabel}`"
          class="user-profile-modal__photo"
        />
        <div v-else class="user-profile-modal__photo-fallback">
          <span>{{ keywordInitial }}</span>
        </div>
      </div>

      <template v-if="keyword">
        <p class="user-profile-modal__keyword">
          <span class="user-profile-modal__keyword-label">Palavra-chave:</span>
          <span class="user-profile-modal__keyword-value">{{ keyword }}</span>
        </p>

        <p v-if="animalLabel" class="user-profile-modal__animal-name">
          Animal: {{ animalLabel }}
          <span v-if="scientificName" class="user-profile-modal__scientific">({{ scientificName }})</span>
        </p>

        <p class="user-profile-modal__description">
          Esta palavra identifica sua conta no 2FA e pode ser solicitada em operações críticas,
          junto com o código do autenticador. Não compartilhe com terceiros.
        </p>

        <p v-if="attribution" class="user-profile-modal__attribution">
          Foto via iNaturalist — {{ attribution }}
        </p>
      </template>

      <div class="user-profile-modal__section">
        <p class="user-profile-modal__section-title">Tema de cores</p>
        <div class="user-profile-modal__themes">
          <button
            v-for="theme in themes"
            :key="theme.id"
            type="button"
            class="user-profile-modal__theme-btn"
            :class="{ 'user-profile-modal__theme-btn--active': activeTheme === theme.id }"
            :title="theme.label"
            :aria-label="theme.label"
            @click="setTheme(theme.id)"
          >
            <span class="user-profile-modal__theme-dot" :style="{ background: theme.preview }" />
            <span class="user-profile-modal__theme-label">{{ theme.label }}</span>
          </button>
        </div>
      </div>
    </base-card>
    </base-modal>
  </body-portal>
</template>

<script>
import BodyPortal from '@/components/layout/BodyPortal.vue';
import { getAnimalSearchLabel, normalizeAnimalKeyword } from '@/lib/animalKeywordLabels';
import {
  APP_THEMES,
  getSavedThemeId,
  saveAppTheme,
} from '@/lib/appThemes';

export default {
  name: 'AnimalKeywordPhotoModal',
  components: {
    BodyPortal,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    keyword: {
      type: String,
      default: '',
    },
    photoUrl: {
      type: String,
      default: '',
    },
    attribution: {
      type: String,
      default: '',
    },
    commonName: {
      type: String,
      default: '',
    },
    scientificName: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      themes: APP_THEMES,
      activeTheme: getSavedThemeId(),
    };
  },
  computed: {
    visible: {
      get() {
        return this.show;
      },
      set(value) {
        this.$emit('update:show', value);
      },
    },
    animalLabel() {
      return this.commonName || getAnimalSearchLabel(this.keyword);
    },
    keywordInitial() {
      const normalized = normalizeAnimalKeyword(this.keyword);
      return normalized.charAt(0) || '?';
    },
  },
  watch: {
    show(isOpen) {
      if (isOpen) {
        this.activeTheme = getSavedThemeId();
      }
    },
  },
  methods: {
    close() {
      this.visible = false;
    },
    setTheme(themeId) {
      this.activeTheme = saveAppTheme(themeId);
    },
  },
};
</script>

<style scoped>
.user-profile-modal__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--spacing-md);
}

.user-profile-modal__title {
  margin: 0;
  color: var(--text-secondary);
  font-size: 1.1rem;
  font-weight: var(--font-weight-semibold);
}

.user-profile-modal__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: var(--border-radius-sm);
  background: transparent;
  color: var(--text-muted);
  cursor: pointer;
}

.user-profile-modal__close:hover {
  background: var(--state-hover);
  color: var(--text-primary);
}

.user-profile-modal__photo-wrap {
  width: 100%;
  max-height: 280px;
  border-radius: var(--border-radius-md);
  overflow: hidden;
  background: var(--state-hover);
  margin-bottom: var(--spacing-md);
}

.user-profile-modal__photo {
  display: block;
  width: 100%;
  max-height: 280px;
  object-fit: cover;
}

.user-profile-modal__photo-fallback {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 180px;
  font-size: 3rem;
  font-weight: var(--font-weight-bold);
  color: var(--text-muted);
}

.user-profile-modal__keyword {
  margin: 0 0 var(--spacing-sm);
  color: var(--text-primary);
  font-size: 1rem;
}

.user-profile-modal__keyword-label {
  margin-right: var(--spacing-xs);
  color: var(--text-muted);
}

.user-profile-modal__keyword-value {
  font-weight: var(--font-weight-bold);
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

.user-profile-modal__animal-name {
  margin: 0 0 var(--spacing-sm);
  color: var(--text-primary);
  font-weight: var(--font-weight-medium);
}

.user-profile-modal__scientific {
  color: var(--text-muted);
  font-style: italic;
  font-weight: var(--font-weight-normal);
}

.user-profile-modal__description {
  margin: 0 0 var(--spacing-sm);
  color: var(--text-muted);
  font-size: 0.9rem;
  line-height: 1.45;
}

.user-profile-modal__attribution {
  margin: 0 0 var(--spacing-md);
  color: var(--text-muted);
  font-size: 0.72rem;
  line-height: 1.35;
  opacity: 0.85;
}

.user-profile-modal__section {
  margin-top: var(--spacing-md);
  padding-top: var(--spacing-md);
  border-top: 1px solid var(--border-sidebar);
}

.user-profile-modal__section-title {
  margin: 0 0 var(--spacing-sm);
  color: var(--text-muted);
  font-size: 0.75rem;
  font-weight: var(--font-weight-semibold);
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.user-profile-modal__themes {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--spacing-sm);
}

.user-profile-modal__theme-btn {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  width: 100%;
  padding: var(--spacing-sm);
  border: 1px solid var(--border-sidebar);
  border-radius: var(--border-radius-sm);
  background: transparent;
  color: var(--text-primary);
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
}

.user-profile-modal__theme-btn:hover {
  background: var(--state-hover);
}

.user-profile-modal__theme-btn--active {
  border-color: var(--color-primary);
  background: var(--state-active);
}

.user-profile-modal__theme-dot {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  flex-shrink: 0;
  border: 1px solid rgba(255, 255, 255, 0.35);
}

.user-profile-modal__theme-label {
  font-size: 0.78rem;
  text-align: left;
  line-height: 1.2;
}
</style>
